// ─────────────────────────────────────────────────────────────
//  SCÉNARIO DE DÉMONSTRATION — SÉQUENCE SCRIPTÉE
//
//  Délibérément scripté, pas branché sur un LLM : une démo de vente
//  doit être déterministe. Même rythme, même résultat, aucune latence.
//  Le vrai modèle arrive au MVP.
//
//  Pour ajouter les lots suivants : ajouter des beats à la fin.
//  La coquille n'a pas à changer.
// ─────────────────────────────────────────────────────────────

export type ArtifactTab = 'profil' | 'description' | 'criteres'

export type Beat = {
  id: string
  /** Qui parle, ou rien si le beat ne fait qu'agir sur le panneau. */
  role?: 'user' | 'assistant'
  text?: string
  /** Puces extraites, affichées sous le message de Talenxia. */
  chips?: string[]
  /** Affiche l'encadré des critères écartés. */
  showExcluded?: boolean
  /** Ouvre un onglet du panneau d'artefacts. */
  openTab?: ArtifactTab
  /** Nombre de blocs révélés du profil de réussite (0–9). */
  profileBlocks?: number
  /** Révèle la description de poste. */
  showDescription?: boolean
  /** Nombre de critères révélés (0–7). */
  criteriaCount?: number
  /** Pause avant le beat suivant, en ms. */
  delay: number
  /** Marqueur d'étape pour la barre de progression. */
  stage?: string
}

export const openingPrompt =
  "Je cherche un Data Engineer principal à Montréal pour moderniser notre plateforme de données. La personne doit être autonome, capable de construire et pas seulement de maintenir, et à l'aise avec un périmètre encore flou."

export const beats: Beat[] = [
  {
    id: 'b0',
    role: 'user',
    text: openingPrompt,
    delay: 900,
    stage: 'Comprendre',
  },
  {
    id: 'b1',
    role: 'assistant',
    text: "Ce n'est pas une description de poste que vous me demandez. C'est un profil de réussite — une hypothèse sur ce qui fera qu'une personne réussira ici, dans les 90 premiers jours. Je la construis à droite pendant qu'on se parle.",
    delay: 2600,
  },
  {
    id: 'b2',
    role: 'assistant',
    text: "Voici ce que j'ai retenu de votre demande.",
    chips: [
      'Data Engineer principal',
      'Données et analytique',
      'Montréal · hybride',
      'Construire, pas maintenir',
      'Autonomie requise',
      'Périmètre encore flou',
    ],
    openTab: 'profil',
    profileBlocks: 2,
    delay: 2400,
  },
  {
    id: 'b3',
    role: 'assistant',
    text: "J'ai aussi écarté quatre critères avant même de chercher.",
    showExcluded: true,
    profileBlocks: 3,
    delay: 3200,
  },
  {
    id: 'b4',
    role: 'assistant',
    text: "Trois questions et j'ai ce qu'il me faut. Qu'est-ce qui est le plus difficile dans ce poste ?",
    profileBlocks: 4,
    delay: 2200,
  },
  {
    id: 'b5',
    role: 'user',
    text: "Le périmètre n'est pas défini. La personne précédente est partie pour ça.",
    delay: 1800,
  },
  {
    id: 'b6',
    role: 'assistant',
    text: "Noté. Je monte l'autonomie au rang de critère essentiel et j'ajoute un risque à valider en entrevue. Deuxième question : à quoi verrez-vous dans 90 jours que c'était la bonne personne ?",
    profileBlocks: 6,
    delay: 2800,
  },
  {
    id: 'b7',
    role: 'user',
    text: "Le pipeline de données clients tourne en production, sans que j'aie eu à arbitrer chaque décision.",
    delay: 1900,
  },
  {
    id: 'b8',
    role: 'assistant',
    text: "C'est mesurable. J'en fais un indicateur de réussite à 90 jours — c'est lui que je confronterai au résultat réel. Dernière question : quelles compétences peuvent s'apprendre après l'embauche ?",
    profileBlocks: 8,
    delay: 2800,
  },
  {
    id: 'b9',
    role: 'user',
    text: "Snowflake et Terraform s'apprennent. Spark et Airflow, non.",
    delay: 1700,
  },
  {
    id: 'b10',
    role: 'assistant',
    text: "Le profil de réussite est complet. Neuf blocs, dont trois issus de vos réponses.",
    profileBlocks: 9,
    delay: 2600,
    stage: 'Construire',
  },
  {
    id: 'b11',
    role: 'assistant',
    text: "J'en tire la description de poste. Chaque section renvoie au bloc du profil dont elle découle — vous pouvez tout modifier, changer le ton, ou la traduire.",
    openTab: 'description',
    showDescription: true,
    delay: 3400,
  },
  {
    id: 'b12',
    role: 'assistant',
    text: "Voici les sept critères que je recommande. Ce sont les mêmes du sourcing jusqu'au résultat à 90 jours — c'est cette continuité qui rend la prédiction mesurable.",
    openTab: 'criteres',
    criteriaCount: 7,
    delay: 3000,
    stage: 'Évaluer',
  },
  {
    id: 'b13',
    role: 'assistant',
    text: "Prochaine étape : la stratégie de sourcing, puis l'approche des talents et leurs consentements. Aucun nom ne vous sera montré avant qu'un consentement soit obtenu.",
    delay: 0,
  },
]

/* ══ CONTENU DU PROFIL DE RÉUSSITE — 9 BLOCS ══════════════════════ */

export type ProfileBlock = {
  title: string
  items: string[]
  /** Issu d'une réponse de l'utilisateur, pas de la demande initiale. */
  fromAnswer?: boolean
}

export const profileBlocks: ProfileBlock[] = [
  {
    title: 'Mission du poste',
    items: [
      "Reconstruire la plateforme de données et la porter en production, dans un périmètre qui se précisera en cours de route.",
    ],
  },
  {
    title: 'Résultats attendus',
    items: [
      'Pipeline de données clients en production',
      'Architecture documentée et reprise par l’équipe',
      'Réduction du délai de mise à disposition des données',
    ],
  },
  {
    title: 'Compétences essentielles',
    items: ['Spark', 'Airflow', 'Modélisation de données', 'Conception d’architecture'],
  },
  {
    title: 'Comportements recherchés',
    items: ['Avance sans arbitrage constant', 'Décide en information incomplète', 'Documente sans qu’on le demande'],
  },
  {
    title: 'Contexte de travail',
    items: ['Hybride · 1 jour par semaine à Montréal', 'Équipe de 6 · en construction', 'Périmètre flou 6 à 9 mois'],
  },
  {
    title: 'Risques à valider',
    items: [
      'Tolérance réelle à l’ambiguïté — cause du départ précédent',
      'Capacité à construire sans structure existante',
    ],
    fromAnswer: true,
  },
  {
    title: 'Facteurs de rétention',
    items: ['Autonomie réelle sur l’architecture', 'Visibilité auprès de la direction', 'Budget outillage'],
  },
  {
    title: 'Indicateurs à 30 / 60 / 90 jours',
    items: [
      'J30 — diagnostic posé, plan partagé',
      'J60 — premier flux en préproduction',
      'J90 — pipeline clients en production, sans arbitrage du gestionnaire',
    ],
    fromAnswer: true,
  },
  {
    title: 'Compétences pouvant être apprises',
    items: ['Snowflake', 'Terraform', 'Gouvernance de données'],
    fromAnswer: true,
  },
]

/* ══ DESCRIPTION DE POSTE ════════════════════════════════════ */

export const jobDescription = [
  {
    heading: 'Le mandat',
    source: 'Mission du poste',
    body: "Vous reconstruisez notre plateforme de données et vous la portez en production. Le périmètre se précisera en cours de route — nous cherchons quelqu'un que ça stimule plutôt que ça inquiète.",
  },
  {
    heading: 'Ce que vous aurez livré dans 90 jours',
    source: 'Indicateurs à 30 / 60 / 90 jours',
    body: "Le pipeline de données clients tourne en production. L'architecture est documentée et l'équipe peut la reprendre. Vous y serez arrivé sans avoir eu besoin d'un arbitrage à chaque décision.",
  },
  {
    heading: 'Ce qu’il vous faut',
    source: 'Compétences essentielles',
    body: "Spark et Airflow en profondeur. Une expérience de construction, pas seulement de maintenance. Snowflake et Terraform s'apprennent chez nous — ne vous autocensurez pas là-dessus.",
  },
  {
    heading: 'Le contexte',
    source: 'Contexte de travail',
    body: "Équipe de six, en construction. Hybride, avec une journée par semaine à Montréal. Autonomie réelle sur l'architecture et un budget d'outillage.",
  },
]
